import type { ReactNode } from 'react';
import type { ToolUseItem } from '../../../../contracts/harness.types';
import {
  directoryLeafTool,
  isEligibilityLeafTool,
  parseEligibilityPayload,
  type EligibilityPayload,
} from '../../../../contracts/insuranceArtifacts';
import type { Artifact } from '../../../../data/projects/artifacts/state';
import {
  friendlyToolError,
  isRecoverableContextPrompt,
} from '../../../../data/projects/components/projectChatMessage.helpers';
import { readConnectRequired } from '../../../../data/projects/integrations/connectRequired';
import { ConnectRequiredBlock } from '../../components/ConnectRequiredBlock';
import { toolResultText } from '../../harness/artifactFromTool';
import { EligibilityCoverageBlock } from '../EligibilityCoverageBlock';
import { isRecord, readToolData } from './shared/readers';
import { toFetchingStatus } from './status';

/**
 * Directory-routed Availity coverage tools (`integrations.apps.read|write`
 * with leaf `integrations.coverage.*`). Returns null for other leaves so
 * the generic tool row can render.
 */
export const renderCoverageEligibilityToolUse = (
  item: ToolUseItem,
  onOpenArtifact?: (artifact: Artifact) => void
): ReactNode => {
  const leaf = directoryLeafTool(item.name, item.input);
  if (!isEligibilityLeafTool(leaf)) return null;

  const status = toFetchingStatus(item.status);
  if (!status) return null;

  if (status === 'fetching') {
    return <EligibilityCoverageBlock status="fetching" />;
  }

  if (status === 'error') {
    const connect = readConnectRequired(item);
    if (connect) {
      return (
        <ConnectRequiredBlock
          payload={connect}
          error={friendlyToolError(toolResultText(item))}
          toolCallId={item.id}
        />
      );
    }
    if (isRecoverableContextPrompt(toolResultText(item))) return null;
    return (
      <EligibilityCoverageBlock
        status="error"
        error={friendlyToolError(toolResultText(item))}
      />
    );
  }

  const payload = readEligibilityPayload(item);
  if (!payload) return null;

  return (
    <EligibilityCoverageBlock
      status="completed"
      payload={payload}
      onViewAll={
        onOpenArtifact
          ? (next) =>
              onOpenArtifact({
                type: 'insurance',
                variant: 'eligibility',
                patients: next.patients,
                provider: next.provider,
                query: next.query,
                warning: next.warning,
              })
          : undefined
      }
    />
  );
};

const readEligibilityPayload = (item: ToolUseItem): EligibilityPayload | null => {
  const data = readToolData(item);
  const input = isRecord(item.input) ? item.input : undefined;
  return parseEligibilityPayload(data, input);
};
